import BoldSmallText from '@/components/BoldSmallText';
import RegularText from '@/components/RegularText';
import Switch from '@/components/Switch';
import { BORDER_RADIUS_OPTIONS, DISPLAY_TYPE_OPTIONS, LAYOUT_OPTIONS } from '@/constants/enum';
import { handleSaveWidgetSettings, widgetSettingSelector } from '@/redux/slice/widget.slice';
import { BlockStack, Box, InlineGrid, InlineStack, Select } from '@shopify/polaris';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

export const WidgetMethod = () => {
  const { t } = useTranslation('widget_settings');
  const widgetSettings = useSelector(widgetSettingSelector);
  const dispatch = useDispatch();

  const displayTypeOptions = useMemo(
    () =>
      Object.values(DISPLAY_TYPE_OPTIONS).map((option) => ({
        label: t(`widgetMethods.displayType.options.${option}`),
        value: option,
      })),
    [t],
  );

  const layoutOptions = useMemo(
    () =>
      Object.values(LAYOUT_OPTIONS).map((option) => ({
        label: t(`widgetMethods.layout.options.${option}`),
        value: option,
      })),
    [t],
  );

  const borderOptions = useMemo(
    () =>
      Object.values(BORDER_RADIUS_OPTIONS).map((option) => ({
        label: t(`widgetMethods.borderRadius.options.${option}`),
        value: option,
      })),
    [t],
  );

  const hideCheckIconOptions = useMemo(
    () => [
      {
        label: t('widgetDesign.general.method.hideCheckIcon.options.true'),
        value: 'true',
      },
      {
        label: t('widgetDesign.general.method.hideCheckIcon.options.false'),
        value: 'false',
      },
    ],
    [t],
  );

  return (
    <BlockStack gap={'300'}>
      <BoldSmallText>{t('widgetDesign.general.method.title')}</BoldSmallText>
      <Box>
        <InlineStack align="space-between">
          <RegularText fontWeight="medium">{t('widgetDesign.general.method.hidePickupLocation.title')}</RegularText>
          <Switch
            isActive={widgetSettings.widgetMethods.hidePickupLocation}
            onSwitch={() =>
              dispatch(
                handleSaveWidgetSettings({
                  widgetMethods: {
                    ...widgetSettings.widgetMethods,
                    hidePickupLocation: !widgetSettings.widgetMethods.hidePickupLocation,
                  },
                }),
              )
            }
          />
        </InlineStack>
        <RegularText tone="subdued">{t('widgetDesign.general.method.hidePickupLocation.helpText')}</RegularText>
      </Box>
      <Box>
        <InlineStack align="space-between">
          <RegularText fontWeight="medium">{t('widgetDesign.general.method.hideHeader.title')}</RegularText>
          <Switch
            isActive={!widgetSettings.widgetMethods.hideHeader}
            onSwitch={() => {
              dispatch(
                handleSaveWidgetSettings({
                  widgetMethods: {
                    ...widgetSettings.widgetMethods,
                    hideHeader: !widgetSettings.widgetMethods.hideHeader,
                  },
                }),
              );
            }}
          />
        </InlineStack>
        <RegularText tone="subdued">{t('widgetDesign.general.method.hideHeader.helpText')}</RegularText>
      </Box>
      <Box>
        <InlineStack align="space-between">
          <RegularText fontWeight="medium">{t('widgetDesign.general.method.hideHeadingTitle.title')}</RegularText>
          <Switch
            isActive={!widgetSettings.widgetMethods.hideHeadingTitle}
            onSwitch={() => {
              dispatch(
                handleSaveWidgetSettings({
                  widgetMethods: {
                    ...widgetSettings.widgetMethods,
                    hideHeadingTitle: !widgetSettings.widgetMethods.hideHeadingTitle,
                  },
                }),
              );
            }}
          />
        </InlineStack>
        <RegularText tone="subdued">{t('widgetDesign.general.method.hideHeadingTitle.helpText')}</RegularText>
      </Box>
      <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">
        <Select
          label={t('widgetDesign.general.method.displayType.label')}
          options={displayTypeOptions}
          value={widgetSettings.widgetMethods.displayType}
          onChange={(value: DISPLAY_TYPE_OPTIONS) => {
            dispatch(
              handleSaveWidgetSettings({
                widgetMethods: {
                  ...widgetSettings.widgetMethods,
                  displayType: value,
                },
              }),
            );
          }}
        />
        <Select
          label={t('widgetDesign.general.method.layout.label')}
          options={layoutOptions}
          value={widgetSettings.widgetMethods.layout}
          onChange={(value: LAYOUT_OPTIONS) => {
            dispatch(
              handleSaveWidgetSettings({
                widgetMethods: {
                  ...widgetSettings.widgetMethods,
                  layout: value,
                },
              }),
            );
          }}
        />
      </InlineGrid>
      <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">
        <Select
          label={t('widgetDesign.general.method.borderRadius.label')}
          options={borderOptions}
          value={widgetSettings.widgetMethods.borderRadius}
          onChange={(value: BORDER_RADIUS_OPTIONS) => {
            dispatch(
              handleSaveWidgetSettings({
                widgetMethods: {
                  ...widgetSettings.widgetMethods,
                  borderRadius: value,
                },
              }),
            );
          }}
        />
        <Select
          label={t('widgetDesign.general.method.hideCheckIcon.label')}
          options={hideCheckIconOptions}
          value={widgetSettings.widgetMethods.hideCheckIcon.toString()}
          onChange={(value: string) => {
            dispatch(
              handleSaveWidgetSettings({
                widgetMethods: {
                  ...widgetSettings.widgetMethods,
                  hideCheckIcon: value === 'true' ? true : false,
                },
              }),
            );
          }}
        />
      </InlineGrid>
    </BlockStack>
  );
};
